import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import { 
  Heart, AlertTriangle, Phone, Video, Pill, Clock, CheckCircle2, 
  Target, BookOpen, Smile, Frown, Meh, Activity, Droplet, 
  Home, Utensils, Moon, Sun, Shield, RefreshCw, MessageCircle,
  PlayCircle, ChevronRight, Star, Calendar, User
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

// Zero-tap symptom logging scales
const SYMPTOM_SCALES = {
  pain: { name: 'Pain', icon: Activity, color: 'red', levels: ['None', 'Mild', 'Moderate', 'Severe', 'Extreme'] },
  agitation: { name: 'Agitation', icon: AlertTriangle, color: 'orange', levels: ['Calm', 'Restless', 'Agitated', 'Very Agitated', 'Distressed'] },
  dyspnea: { name: 'Breathing', icon: Heart, color: 'blue', levels: ['Easy', 'Slight', 'Moderate', 'Labored', 'Severe'] }
};

// Comfort medication videos
const COMFORT_VIDEOS = {
  pain: { title: 'Pain Relief Administration', duration: '30 sec', thumbnail: '/videos/pain-relief.jpg' },
  nausea: { title: 'Anti-Nausea Medication', duration: '30 sec', thumbnail: '/videos/nausea.jpg' },
  anxiety: { title: 'Calming Medication Guide', duration: '30 sec', thumbnail: '/videos/anxiety.jpg' },
  secretions: { title: 'Managing Secretions', duration: '30 sec', thumbnail: '/videos/secretions.jpg' }
};

const CaregiverCommandCenter = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('daily');
  const [confidenceScore, setConfidenceScore] = useState(null);
  const [symptomToLog, setSymptomToLog] = useState(null);
  const [symptomSeverity, setSymptomSeverity] = useState(5);
  const [showVideo, setShowVideo] = useState(null);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [preferredTimes, setPreferredTimes] = useState({});
  const { toast } = useToast();

  const token = localStorage.getItem('token');

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/phase38-44/caregiver/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Zero-tap symptom logging
  const logSymptom = async (symptomType, severity) => {
    try {
      const res = await fetch(`${API}/api/phase38-44/caregiver/symptom/log?patient_id=${dashboard?.patient_id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          symptom: symptomType,
          severity: severity,
          description: `Logged via zero-tap: ${SYMPTOM_SCALES[symptomType]?.levels[Math.floor(severity/2.5)] || 'Unknown'}`
        })
      });

      if (res.ok) {
        toast({
          title: "Symptom Logged",
          description: `${symptomType} has been recorded and synced to your care team.`,
        });
        setSymptomToLog(null);
        
        // Show relevant video if severe
        if (severity >= 7) {
          setShowVideo(symptomType === 'pain' ? 'pain' : symptomType === 'agitation' ? 'anxiety' : 'secretions');
        }
        
        fetchDashboard();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to log symptom", variant: "destructive" });
    }
  };

  // Request support (Panic-to-Peace button)
  const requestSupport = async (priority) => {
    try {
      const res = await fetch(`${API}/api/phase38-44/caregiver/request-support`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          patient_id: dashboard?.patient_id,
          priority: priority,
          description: priority === 'emergency' ? 'Emergency assistance needed' : 'Need support'
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast({
          title: priority === 'emergency' ? "🚨 Help is Coming" : "Support Requested",
          description: data.triggers_video ? 
            "Connecting you with a nurse via video..." : 
            "A care team member will contact you shortly.",
        });
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to request support", variant: "destructive" });
    }
  };

  // Log task completion
  const completeTask = async (taskId) => {
    try {
      const res = await fetch(`${API}/api/phase38-44/caregiver/task/${taskId}/complete?patient_id=${dashboard?.patient_id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast({ title: "Task Completed", description: "Great job! Task has been logged." });
        fetchDashboard();
      }
    } catch (err) {
      console.error('Error completing task:', err);
    }
  };

  // Log medication
  const logMedication = async (medId, scheduledTime, given = true, skipReason = null) => {
    try {
      const url = new URL(`${API}/api/phase38-44/caregiver/medication/log`);
      url.searchParams.append('patient_id', dashboard?.patient_id);
      url.searchParams.append('medication_id', medId);
      url.searchParams.append('scheduled_time', scheduledTime);
      url.searchParams.append('given', given);
      if (skipReason) url.searchParams.append('skip_reason', skipReason);

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast({ 
          title: given ? "Medication Logged" : "Medication Skipped",
          description: given ? "The medication has been recorded." : "The nurse has been notified."
        });
        fetchDashboard();
      }
    } catch (err) {
      console.error('Error logging medication:', err);
    }
  };

  // Submit confidence score
  const submitConfidenceScore = async (score) => {
    setConfidenceScore(score);
    if (score <= 3) {
      toast({
        title: "We're Here to Help",
        description: "Let's connect you with support resources.",
        action: (
          <Button variant="outline" size="sm" onClick={() => requestSupport('routine')}>
            Chat with Nurse
          </Button>
        ),
      });
    } else {
      toast({
        title: `You're doing a great job, ${dashboard?.patient_name?.split(' ')[0] || 'Caregiver'}!`,
        description: "Here's what's next on your care plan.",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const tasks = dashboard?.dashboard?.daily_tasks || { tasks: {}, total: 0, completed: 0, completion_rate: 0 };
  const medications = dashboard?.dashboard?.medications || [];
  const goals = dashboard?.dashboard?.goals || [];
  const upcomingVisits = dashboard?.dashboard?.upcoming_visits || [];
  const careGuides = dashboard?.dashboard?.care_guides || [];

  return (
    <div className="space-y-6 p-4 bg-gradient-to-br from-amber-50 via-white to-teal-50 min-h-screen">
      {/* Header with Panic Button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Caring for {dashboard?.patient_name || 'Your Loved One'}
          </h1>
          <p className="text-gray-600 text-sm">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="border-teal-500 text-teal-700 hover:bg-teal-50"
            onClick={() => requestSupport('routine')}
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Message Team
          </Button>
          <Button 
            className="bg-red-500 hover:bg-red-600 text-white shadow-lg"
            onClick={() => requestSupport('emergency')}
            data-testid="panic-button"
          >
            <Phone className="h-4 w-4 mr-2" />
            Get Help Now
          </Button>
        </div>
      </div>

      {/* Daily Confidence Check */}
      {confidenceScore === null && (
        <Card className="bg-gradient-to-r from-amber-100 to-orange-100 border-amber-200">
          <CardContent className="p-6">
            <h3 className="font-semibold text-amber-900 mb-3">Daily Check-In</h3>
            <p className="text-amber-800 mb-4">How prepared do you feel to manage symptoms today?</p>
            <div className="flex gap-2 flex-wrap">
              {[1,2,3,4,5].map(score => (
                <Button 
                  key={score}
                  variant="outline" 
                  className="flex-1 min-w-[60px] border-amber-300 hover:bg-amber-200"
                  onClick={() => submitConfidenceScore(score)}
                >
                  {score === 1 && <Frown className="h-5 w-5 text-red-500" />}
                  {score === 2 && <Frown className="h-5 w-5 text-orange-500" />}
                  {score === 3 && <Meh className="h-5 w-5 text-yellow-500" />}
                  {score === 4 && <Smile className="h-5 w-5 text-lime-500" />}
                  {score === 5 && <Smile className="h-5 w-5 text-green-500" />}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Zero-Tap Symptom Logging */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5 text-teal-600" />
            Quick Symptom Check
          </CardTitle>
          <CardDescription>Tap to log how your loved one is feeling right now</CardDescription>
        </CardHeader>
        <CardContent>
          {symptomToLog ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-medium">{SYMPTOM_SCALES[symptomToLog].name} Level</span>
                <Button variant="ghost" size="sm" onClick={() => setSymptomToLog(null)}>Cancel</Button>
              </div>
              <div className="flex justify-between text-sm text-gray-500 px-2">
                {SYMPTOM_SCALES[symptomToLog].levels.map((level, i) => (
                  <span key={i} className={symptomSeverity >= i*2.5 ? 'font-medium text-gray-800' : ''}>{level}</span>
                ))}
              </div>
              <Slider
                value={[symptomSeverity]}
                onValueChange={([v]) => setSymptomSeverity(v)}
                max={10}
                step={1}
                className="py-4"
              />
              <Button 
                className="w-full bg-teal-600 hover:bg-teal-700"
                onClick={() => logSymptom(symptomToLog, symptomSeverity)}
              >
                Log {SYMPTOM_SCALES[symptomToLog].name}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(SYMPTOM_SCALES).map(([key, { name, icon: Icon, color }]) => (
                <Button
                  key={key}
                  variant="outline"
                  className={`h-20 flex-col gap-2 border-2 hover:border-${color}-400 hover:bg-${color}-50`}
                  onClick={() => { setSymptomToLog(key); setSymptomSeverity(5); }}
                  data-testid={`symptom-${key}`}
                >
                  <Icon className={`h-6 w-6 text-${color}-500`} />
                  <span className="text-xs">{name}</span>
                </Button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Just-in-Time Education Video */}
      {showVideo && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex gap-3">
                <div className="bg-blue-100 rounded-lg p-3">
                  <PlayCircle className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-blue-900">Quick Help Video</h4>
                  <p className="text-sm text-blue-700">{COMFORT_VIDEOS[showVideo]?.title}</p>
                  <p className="text-xs text-blue-600">{COMFORT_VIDEOS[showVideo]?.duration}</p>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setShowVideo(null)}>
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="daily">Daily Care</TabsTrigger>
          <TabsTrigger value="meds">Medications</TabsTrigger>
          <TabsTrigger value="goals">Goals</TabsTrigger>
          <TabsTrigger value="guides">Guides</TabsTrigger>
        </TabsList>

        {/* Daily Care Tasks */}
        <TabsContent value="daily" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg">Today's Care Tasks</CardTitle>
                <Badge variant="outline" className="text-teal-600">
                  {tasks.completion_rate}% Complete
                </Badge>
              </div>
              <Progress value={tasks.completion_rate} className="h-2" />
            </CardHeader>
            <CardContent className="space-y-4">
              {/* ADL Tasks */}
              {tasks.tasks?.adl_tasks?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Home className="h-4 w-4" /> Daily Living
                  </h4>
                  <div className="grid gap-2">
                    {tasks.tasks.adl_tasks.map(task => (
                      <div 
                        key={task.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          task.completed ? 'bg-green-50 border-green-200' : 'bg-white'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {task.completed ? (
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                          ) : (
                            <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                          )}
                          <span className={task.completed ? 'text-gray-500 line-through' : ''}>
                            {task.name}
                          </span>
                        </div>
                        {!task.completed && (
                          <Button size="sm" variant="ghost" onClick={() => completeTask(task.id)}>
                            Done
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upcoming Visits */}
              {upcomingVisits.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Calendar className="h-4 w-4" /> Upcoming Visits
                  </h4>
                  <div className="space-y-2">
                    {upcomingVisits.slice(0, 3).map((visit, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <User className="h-5 w-5 text-blue-500" />
                          <div>
                            <p className="font-medium">{visit.staff_name || visit.type}</p>
                            <p className="text-sm text-gray-600">{visit.date} at {visit.time}</p>
                          </div>
                        </div>
                        {scheduleMode && (
                          <Button size="sm" variant="outline">Reschedule</Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* In Charge Toggle */}
              <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-purple-900">Schedule Preferences</h4>
                      <p className="text-sm text-purple-700">Set your preferred visit times</p>
                    </div>
                    <Button 
                      variant={scheduleMode ? "default" : "outline"}
                      className={scheduleMode ? "bg-purple-600" : "border-purple-300"}
                      onClick={() => setScheduleMode(!scheduleMode)}
                    >
                      {scheduleMode ? "Save Times" : "Customize"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Medications */}
        <TabsContent value="meds" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Pill className="h-5 w-5 text-teal-600" />
                Today's Medications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {medications.length > 0 ? medications.map((med, i) => (
                  <div 
                    key={i}
                    className={`p-4 rounded-lg border ${
                      med.given ? 'bg-green-50 border-green-200' : 
                      med.skipped ? 'bg-red-50 border-red-200' : 'bg-white'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{med.name}</h4>
                          {med.is_prn && <Badge variant="outline">PRN</Badge>}
                        </div>
                        <p className="text-sm text-gray-600">{med.dosage} - {med.route}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          <Clock className="h-3 w-3 inline mr-1" />
                          Scheduled: {med.scheduled_time}
                        </p>
                        {med.instructions && (
                          <p className="text-xs text-blue-600 mt-1">{med.instructions}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {!med.given && !med.skipped && (
                          <>
                            <Button 
                              size="sm" 
                              className="bg-green-500 hover:bg-green-600"
                              onClick={() => logMedication(med.medication_id, med.scheduled_time, true)}
                            >
                              Given
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="border-red-300 text-red-600"
                              onClick={() => logMedication(med.medication_id, med.scheduled_time, false, 'Patient refused')}
                            >
                              Skip
                            </Button>
                          </>
                        )}
                        {med.given && (
                          <Badge className="bg-green-500">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Given at {new Date(med.given_at).toLocaleTimeString()}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                )) : (
                  <p className="text-center text-gray-500 py-6">No medications scheduled for today</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Patient Goals */}
        <TabsContent value="goals" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="h-5 w-5 text-teal-600" />
                What Makes a Good Day
              </CardTitle>
              <CardDescription>Goals set for your loved one's comfort and quality of life</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {goals.length > 0 ? goals.map((goal, i) => (
                  <div key={i} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-medium">{goal.title}</h4>
                        <p className="text-sm text-gray-600">{goal.what_good_day_looks_like}</p>
                      </div>
                      <Badge variant={goal.progress >= 100 ? 'default' : 'outline'}>
                        {goal.progress}%
                      </Badge>
                    </div>
                    <Progress value={goal.progress} className="h-2" />
                    {goal.specific_activities?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {goal.specific_activities.map((activity, j) => (
                          <Badge key={j} variant="secondary">{activity}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )) : (
                  <div className="text-center py-6">
                    <Target className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">No goals set yet</p>
                    <Button variant="outline" className="mt-3">
                      Set a Goal
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Care Guides */}
        <TabsContent value="guides" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-teal-600" />
                Care Guides
              </CardTitle>
              <CardDescription>Step-by-step guides for common care situations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {careGuides.map((guide, i) => (
                  <Button
                    key={i}
                    variant="outline"
                    className="h-auto p-4 justify-between hover:bg-teal-50"
                    onClick={() => window.location.href = `/care-guide/${guide.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-teal-100 p-2 rounded-lg">
                        <BookOpen className="h-5 w-5 text-teal-600" />
                      </div>
                      <span className="font-medium">{guide.name}</span>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CaregiverCommandCenter;
